import React from 'react';
import { bool, oneOf, string, object } from 'prop-types';
import cx from 'classnames';

import Icon from '../Icon';

export const colorProps = [
  'primary',
  'secondary',
  'tertiary',
  'quaternary',
  'white',
  'black',
  'blue',
];

export const typeProps = ['primary', 'secondary', 'tertiary', 'link', 'blue'];

const propTypes = {
  /** Button with same width as height. */
  equal: bool,
  /** Link with href attribute rendered as button. */
  href: string,
  /** Possible positions of icon inside button. */
  iconPosition: oneOf(['left', 'right']),
  /** Active state. */
  isActive: bool,
  /** Disabled state */
  isDisabled: bool,
  /** Button sizes */
  size: oneOf(['small', 'large']),
  /** Button can take one of possible shapes. */
  shape: oneOf(['radius', 'round']),
  /** Button(input) with `type="submit"`. */
  submit: bool,
  /** Html tag in which is button rendered. */
  tag: string,
  /** Visual type of button. */
  type: oneOf(typeProps),
  /** Forwarded DOM element ref. */
  elemRef: object,
  /** Turn on off vertical padding */
  padded: bool,
  /** Background color */
  background: oneOf(colorProps),
  /** Foreground color */
  color: oneOf(colorProps),
  /** Border after button color */
  borderColor: oneOf(colorProps),
};

const CLASS_ROOT = 'btn';

const Button = ({
  className,
  children,
  equal,
  href,
  iconPosition,
  isActive,
  isDisabled,
  size,
  shape,
  submit,
  tag = 'button',
  type,
  elemRef,
  padded,
  background,
  color,
  borderColor,
  ...other
}) => {
  let Tag = tag;
  let buttonType = submit ? 'submit' : 'button';
  let buttonIsDisabled = isDisabled;
  const buttonIconPosition = iconPosition || null;

  if (href) {
    Tag = 'a';
    buttonType = null;
  }

  if (Tag === 'a') {
    buttonType = null;
  }

  const classes = cx(
    CLASS_ROOT,
    {
      [`${CLASS_ROOT}--equal`]: equal,
      [`${CLASS_ROOT}--nopad`]: !padded,
      [`${CLASS_ROOT}--${size}`]: size,
      [`${CLASS_ROOT}--${shape}`]: shape,
      [`${CLASS_ROOT}--${type}`]: type,
      [`${CLASS_ROOT}--background-${background}`]: background,
      [`${CLASS_ROOT}--color-${color}`]: color,
      [`${CLASS_ROOT}--border-${borderColor}`]: borderColor,
      [`${CLASS_ROOT}--icon-${buttonIconPosition}`]: buttonIconPosition,
      'is-active': isActive,
      'is-disabled': Tag === 'a' && isDisabled,
    },
    className
  );

  if (isDisabled && Tag === 'a') {
    buttonIsDisabled = null;
  }

  const props = {
    disabled: buttonIsDisabled,
    href,
    type: !href ? buttonType : null,
    className: classes,
    ref: elemRef,
    ...other,
  };

  const iconProps = {
    primary: {
      default: {
        size: 'medium',
        name: 'arrow-special-rightdown',
        className: 'icon--left text-color-primary icon--default',
      },
      hover: {
        name: 'arrow-special-full',
        className: 'icon--left text-color-primary icon--hover',
      },
    },
    secondary: {
      default: {
        size: 'small',
        name: 'arrow-special-rightdown',
        className: 'icon--left text-color-tertiary icon--default',
      },
      hover: {
        name: 'arrow-special-full',
        className: 'icon--left text-color-tertiary icon--hover',
      },
    },
    tertiary: {
      default: {
        size: 'medium',
        name: 'arrow-special-left',
        className: 'icon--left text-color-primary icon--default',
      },
      hover: {
        name: 'arrow-special-left',
        className: 'icon--left text-color-primary icon--hover',
      },
    },
  };

  const icon = iconProps[type] || null;
  const defaultProps = icon && icon.default;
  const hoverProps = icon && { ...icon.default, ...icon.hover };

  return (
    <Tag {...props}>
      {icon && (
        <React.Fragment>
          <Icon {...defaultProps} />
          <Icon {...hoverProps} />
        </React.Fragment>
      )}
      {children}
      {type === 'link' && <span className="btn__border" />}
    </Tag>
  );
};

Button.displayName = 'Button';
Button.propTypes = propTypes;

export default Button;



// WEBPACK FOOTER //
// ./src/components/Button/Button.js